import React, { useEffect } from 'react';
import { HubCapsule } from '@aws-amplify/core';
import { Link, RouteComponentProps } from '@reach/router';
import { makeStyles, useLoading, useUseCase } from '@smile-next/uikit';
import { LoginForm } from '@/components/LoginForm/LoginForm';
import { LoginFormBody } from '@/components/LoginForm/LoginFormBody';
import { LoginFormFooter } from '@/components/LoginForm/LoginFormFooter';
import { SSOCallbackPageState } from '@/containers/SSOCallback/Page/SSOCallbackPageState';
import { useLocationSearch } from '@/hooks/useLocationSearch';
import { useRouter } from '@/hooks/useRouter';
import { VerifyAuthenticatedUserForSSOCallbackUseCase } from '@/usecases/VerifyAuthenticatedUserForSSOCallbackUseCase';
import { getLambdaErrorCode } from '@/utils/getLambdaErrorCode';
import { registerHubListener } from '@/utils/hubManagement';

const useStyles = makeStyles((theme) => ({
    root: {
        height: 'calc(100vh - 56px)',
        display: 'flex',
        flexDirection: 'column',
    },
    main: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        flex: '1 1 auto',
    },
    link: {
        color: theme.palette.primary.main,
    },
    errorMessage: {
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.error.main,
    },
}));

export const SSOCallbackPage: React.FC<RouteComponentProps> = () => {
    const { state, usecase } = useUseCase(new SSOCallbackPageState());
    const {
        navigateToSelectLoginMethodPageForSSOError,
        navigateToSelectLoginMethodPageForSSOLambdaError,
        movePageAfterSuccessfulSSOLogin,
        toForceSelectLoginPagePath,
    } = useRouter();
    const { error } = useLocationSearch();
    const { startCoverLoading } = useLoading();
    const classes = useStyles();

    useEffect(() => {
        registerHubListener((data: HubCapsule) => {
            if (data.payload.event === 'signIn_failure') {
                const lambdaErrorCode = getLambdaErrorCode(data.payload.data.message);
                navigateToSelectLoginMethodPageForSSOLambdaError(undefined, lambdaErrorCode);
            }
        });
    }, [navigateToSelectLoginMethodPageForSSOLambdaError]);

    useEffect(() => {
        const { endLoading } = startCoverLoading();
        if (error) {
            // エラーが発生した時はログイン方法選択画面に戻りエラーを表示する
            endLoading();
            navigateToSelectLoginMethodPageForSSOError();
            return;
        }

        usecase(new VerifyAuthenticatedUserForSSOCallbackUseCase())
            .execute()
            .then(() => {
                movePageAfterSuccessfulSSOLogin();
            })
            .catch(() => {
                // エラー発生時の処理は別途行っているためここでは何も行わない
            })
            .finally(() => {
                endLoading();
            });
    }, [
        error,
        movePageAfterSuccessfulSSOLogin,
        navigateToSelectLoginMethodPageForSSOError,
        startCoverLoading,
        usecase,
    ]);

    return (
        <div className={classes.root}>
            <div className={classes.main}>
                <LoginForm>
                    {state.errorMessage && (
                        <LoginFormBody>
                            {state.errorMessage && <div className={classes.errorMessage}>{state.errorMessage}</div>}
                        </LoginFormBody>
                    )}
                    <LoginFormFooter>
                        <Link className={classes.link} to={toForceSelectLoginPagePath()}>
                            ログイン画面に戻る
                        </Link>
                    </LoginFormFooter>
                </LoginForm>
            </div>
        </div>
    );
};
